import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
  X,
  Zap,
  Loader2,
  CheckCircle2,
  Copy,
  ExternalLink,
  Mail,
  AlertTriangle,
} from "lucide-react";
import { api } from "@/lib/api";

/**
 * Quick Issue Certificate dialog.
 *
 * Operator-facing fast path: a single form (item name, serial, customer
 * email; optional price + sku) → POST /api/receipts/quick-issue → backend
 * creates the record, mints on Polygon Amoy, and dispatches the email in
 * one round-trip (~10–15s). On success the dialog flips to a
 * confirmation panel showing the certificate ID, the on-chain tx, and
 * the email status with quick "Open Verify" / "Copy Link" actions.
 *
 * Designed to coexist with the existing PDF/JSON upload flow — the
 * Retailer header now exposes both buttons. PDF parsing is unchanged.
 */
export default function QuickIssueDialog({ open, onClose, onIssued }) {
  const [step, setStep] = useState("form"); // form | issuing | success | error
  const [form, setForm] = useState({
    order_id: "",
    item_name: "",
    serial_number: "",
    customer_email: "",
    price: "",
    sku: "",
  });
  const [confirmation, setConfirmation] = useState(null);
  const [errorDetail, setErrorDetail] = useState("");

  useEffect(() => {
    if (!open) {
      // reset on close so reopening starts fresh
      const t = setTimeout(() => {
        setStep("form");
        setForm({ order_id: "", item_name: "", serial_number: "", customer_email: "", price: "", sku: "" });
        setConfirmation(null);
        setErrorDetail("");
      }, 200);
      return () => clearTimeout(t);
    }
  }, [open]);

  if (!open) return null;

  const setField = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    const orderId = form.order_id.trim();
    const item = form.item_name.trim();
    const serial = form.serial_number.trim();
    const email = form.customer_email.trim();
    if (!orderId) return toast.error("Order ID is required");
    if (!item) return toast.error("Item name is required");
    if (!serial) return toast.error("Serial number is required");
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email))
      return toast.error("A valid customer email is required");

    setStep("issuing");
    setErrorDetail("");
    try {
      const payload = {
        order_id: orderId,
        item_name: item,
        serial_number: serial,
        customer_email: email,
      };
      if (form.price !== "" && !Number.isNaN(parseFloat(form.price))) {
        payload.price = parseFloat(form.price);
      }
      if (form.sku.trim()) payload.sku = form.sku.trim();

      const { data } = await api.post("/receipts/quick-issue", payload);
      setConfirmation(data.confirmation || {});
      setStep("success");
      onIssued?.(data);
    } catch (err) {
      const detail =
        err?.response?.data?.detail ||
        err?.message ||
        "Issuance failed. Please retry.";
      setErrorDetail(String(detail));
      setStep("error");
    }
  };

  const copy = (txt, label) => {
    if (!txt) return;
    navigator.clipboard.writeText(txt);
    toast.success(`${label || "Copied"} copied`);
  };

  return (
    <div
      data-testid="quick-issue-modal"
      className="fixed inset-0 z-40 flex items-center justify-center p-4 bg-gray-900/50 backdrop-blur-[2px]"
      onClick={step === "issuing" ? undefined : onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-xl bg-white rounded-2xl shadow-xl border border-gray-200 overflow-hidden"
      >
        <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100 bg-gradient-to-r from-[#0055FF]/5 to-white">
          <div className="flex items-center gap-2.5">
            <span className="inline-flex items-center justify-center w-8 h-8 rounded-lg bg-[#0055FF]/10 text-[#0055FF]">
              <Zap size={16} fill="currentColor" />
            </span>
            <div>
              <div className="font-display font-semibold text-gray-900">
                Quick Issue Certificate
              </div>
              <div className="text-[11px] tracking-wider uppercase text-gray-500">
                Direct mint · ~10s · email auto-sent
              </div>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={step === "issuing"}
            data-testid="quick-issue-close"
            className="p-1.5 rounded-md text-gray-400 hover:text-gray-900 hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <X size={16} />
          </button>
        </div>

        {step === "form" && (
          <form onSubmit={handleSubmit} className="p-6 space-y-4" data-testid="quick-issue-form">
            <Field
              label="Order ID / Receipt ID"
              hint="customer-facing reference"
              required
              testid="qi-order-id"
              value={form.order_id}
              onChange={setField("order_id")}
              placeholder="e.g. POS-10482"
              autoFocus
            />
            <Field
              label="Item name"
              required
              testid="qi-item-name"
              value={form.item_name}
              onChange={setField("item_name")}
              placeholder="e.g. Rolex Submariner Date"
            />
            <Field
              label="Serial number"
              required
              testid="qi-serial"
              value={form.serial_number}
              onChange={setField("serial_number")}
              placeholder="e.g. SN-9F4C82A1"
            />
            <Field
              label="Customer email"
              type="email"
              required
              testid="qi-email"
              value={form.customer_email}
              onChange={setField("customer_email")}
              placeholder="customer@example.com"
            />
            <div className="grid grid-cols-2 gap-3">
              <Field
                label="Price"
                hint="USD · optional"
                testid="qi-price"
                value={form.price}
                onChange={setField("price")}
                placeholder="0.00"
                type="number"
                step="0.01"
                min="0"
              />
              <Field
                label="SKU"
                hint="optional"
                testid="qi-sku"
                value={form.sku}
                onChange={setField("sku")}
                placeholder="ROLEX-SUB"
              />
            </div>
            <div className="pt-2 flex items-center justify-between gap-3 border-t border-gray-100 mt-4">
              <p className="text-[11px] text-gray-500 leading-snug">
                A digital certificate is minted on Polygon Amoy and emailed
                to the customer immediately.
              </p>
              <button
                type="submit"
                data-testid="qi-submit"
                className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#0044CC] transition shadow-sm"
              >
                <Zap size={14} fill="currentColor" />
                Issue Certificate
              </button>
            </div>
          </form>
        )}

        {step === "issuing" && (
          <div
            className="p-10 flex flex-col items-center justify-center text-center"
            data-testid="qi-issuing"
          >
            <Loader2 size={36} className="text-[#0055FF] animate-spin mb-4" />
            <div className="font-display font-semibold text-gray-900 text-lg">
              Issuing certificate…
            </div>
            <div className="text-sm text-gray-500 mt-1.5 max-w-sm">
              Submitting on-chain transaction to Polygon Amoy and preparing
              the customer's certificate email. Please keep this window open.
            </div>
            <div className="mt-5 flex items-center gap-1.5 text-[11px] text-gray-400 font-mono">
              <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#0055FF] animate-pulse" />
              Typical time: 10–15 seconds
            </div>
          </div>
        )}

        {step === "success" && confirmation && (
          <div className="p-6 space-y-5" data-testid="qi-success">
            <div className="flex items-start gap-3">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-emerald-50 text-emerald-600 shrink-0">
                <CheckCircle2 size={22} />
              </span>
              <div>
                <div
                  className="font-display font-semibold text-gray-900 text-lg"
                  data-testid="qi-success-title"
                >
                  Certificate issued
                  {confirmation.email_sent ? " · email sent" : " · email pending"}
                </div>
                <div className="text-sm text-gray-600 mt-1">
                  {confirmation.email_sent ? (
                    <>
                      The certificate has been delivered to{" "}
                      <span className="font-mono text-gray-900">
                        {confirmation.customer_email}
                      </span>
                      .
                    </>
                  ) : (
                    <>
                      Certificate minted, but email delivery failed:{" "}
                      <span className="text-amber-700">
                        {confirmation.email_error || "unknown SMTP error"}
                      </span>
                      . You can resend from the Email Notifications tab.
                    </>
                  )}
                </div>
              </div>
            </div>

            <div className="border border-gray-200 rounded-lg overflow-hidden">
              {confirmation.order_id && (
                <Row
                  label="Order ID"
                  value={confirmation.order_id}
                  testid="qi-success-order-id"
                  onCopy={() => copy(confirmation.order_id, "Order ID")}
                />
              )}
              {confirmation.tx_hash && (
                <Row
                  label="Verification Tx"
                  value={confirmation.tx_hash}
                  mono
                  testid="qi-success-tx"
                  onCopy={() => copy(confirmation.tx_hash, "Tx hash")}
                />
              )}
              <Row
                label="Network"
                value={confirmation.network}
                testid="qi-success-network"
              />
              <Row
                label="Email"
                value={confirmation.customer_email}
                testid="qi-success-email"
                statusIcon={
                  confirmation.email_sent ? (
                    <Mail size={12} className="text-emerald-600" />
                  ) : (
                    <AlertTriangle size={12} className="text-amber-600" />
                  )
                }
              />
            </div>

            <div className="flex items-center justify-end gap-2 pt-1">
              {confirmation.verify_url && (
                <a
                  href={confirmation.verify_url}
                  target="_blank"
                  rel="noreferrer noopener"
                  data-testid="qi-success-verify-link"
                  className="inline-flex items-center gap-1.5 text-sm font-semibold text-[#0055FF] border border-[#0055FF]/30 hover:bg-[#0055FF]/5 px-3 py-2 rounded-md transition"
                >
                  <ExternalLink size={13} />
                  Open Verify Page
                </a>
              )}
              <button
                type="button"
                onClick={onClose}
                data-testid="qi-success-done"
                className="inline-flex items-center gap-1.5 bg-gray-900 text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-black transition"
              >
                Done
              </button>
            </div>
          </div>
        )}

        {step === "error" && (
          <div className="p-6" data-testid="qi-error">
            <div className="flex items-start gap-3 mb-4">
              <span className="inline-flex items-center justify-center w-10 h-10 rounded-full bg-red-50 text-red-600 shrink-0">
                <AlertTriangle size={20} />
              </span>
              <div>
                <div className="font-display font-semibold text-gray-900 text-lg">
                  Issuance failed
                </div>
                <div className="text-sm text-gray-600 mt-1" data-testid="qi-error-message">
                  {errorDetail || "Something went wrong. Please retry."}
                </div>
              </div>
            </div>
            <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setStep("form")}
                data-testid="qi-error-retry"
                className="inline-flex items-center gap-1.5 bg-[#0055FF] text-white text-sm font-semibold px-4 py-2 rounded-md hover:bg-[#0044CC] transition"
              >
                Retry
              </button>
              <button
                type="button"
                onClick={onClose}
                className="inline-flex items-center gap-1.5 text-sm font-semibold text-gray-700 hover:text-gray-900 px-3 py-2 rounded-md"
              >
                Cancel
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function Field({ label, hint, required, testid, type = "text", ...rest }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1.5">
        <span className="text-xs font-semibold text-gray-700">
          {label}
          {required && <span className="text-red-500 ml-0.5">*</span>}
        </span>
        {hint && <span className="text-[10px] uppercase tracking-wider text-gray-400">{hint}</span>}
      </div>
      <input
        type={type}
        data-testid={testid}
        className="w-full px-3 py-2 text-sm bg-white border border-gray-200 rounded-md focus:outline-none focus:border-[#0055FF] focus:ring-2 focus:ring-[#0055FF]/15 transition"
        {...rest}
      />
    </label>
  );
}

function Row({ label, value, mono, testid, onCopy, statusIcon }) {
  if (!value) return null;
  return (
    <div className="flex items-center justify-between px-4 py-2.5 border-b last:border-0 border-gray-100">
      <span className="text-[11px] uppercase tracking-wider text-gray-500 font-semibold flex items-center gap-1.5">
        {statusIcon}
        {label}
      </span>
      <span
        data-testid={testid}
        className={`text-sm text-gray-900 truncate max-w-[55%] flex items-center gap-1.5 ${mono ? "font-mono text-xs" : ""}`}
      >
        <span className="truncate">{value}</span>
        {onCopy && (
          <button
            type="button"
            onClick={onCopy}
            className="text-gray-400 hover:text-[#0055FF] shrink-0"
            title={`Copy ${label}`}
          >
            <Copy size={12} />
          </button>
        )}
      </span>
    </div>
  );
}
